import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import * as planningApi from '../api/planning';
import { useAuth } from './AuthContext';
import type { PlanningCycle, IOPlanSummary } from '../types/planning';

interface PlanningContextValue {
  availableCycles: PlanningCycle[];
  selectedCycle: PlanningCycle | null;
  setSelectedCycle: (cycle: PlanningCycle | null) => void;
  expandedIOIds: Set<number>;
  toggleIOExpand: (ioId: number) => void;
  /** Idempotent expand — add only, never collapses. Used by deep links. */
  ensureIOExpanded: (ioId: number) => void;
  collapseAll: () => void;
  ioSummaries: IOPlanSummary[];
  /** Cycle id that the current `ioSummaries` actually belong to, or null when
   *  none are loaded yet for the selected cycle. Consumers use this to avoid
   *  acting on a previous cycle's summaries during a cycle switch (IOs persist
   *  across cycles, so `io_id` alone can match stale rows). */
  summariesCycleId: number | null;
  loadIOSummaries: (cycleId: number, options?: { silent?: boolean; fresh?: boolean }) => Promise<void>;
  updateIOSummary: (ioId: number, patch: Partial<IOPlanSummary>) => void;
  /** True only when there is no cached list to show for the current cycle. */
  summariesLoading: boolean;
  summariesRefreshing: boolean;
  /** Bumped on manual page Refresh so expanded IO editors reload full detail. */
  summariesRefreshGeneration: number;
  incrementSummariesRefreshGeneration: () => void;
  /** True only when cycles have never been loaded. */
  cyclesLoading: boolean;
  cyclesRefreshing: boolean;
  refreshCycles: () => Promise<void>;
}

const PlanningContext = createContext<PlanningContextValue | null>(null);

const CYCLE_KEY = 'peakLastSelectedCycleId';

/**
 * BI-compliance fields the open IO editor pushes LIVE (via `pushBISummaryPatch`).
 * The stored `/io-summaries` payload can carry a STALE `bi_compliant` flag —
 * e.g. a locked cycle whose `bi_compliance_summary` predates a compliance-logic
 * change (the BP26 depreciation-exclusion case). Left unguarded, a background
 * refresh overwrites the editor's live/correct value and the card badge flickers
 * BI MISMATCH <-> BI COMPLIANT on tab refocus. We snapshot these keys so the
 * live value survives a `/io-summaries` reload.
 */
const BI_OVERRIDE_KEYS = [
  'bi_has_data',
  'bi_compliant',
  'bi_variance',
  'bi_mismatch_months',
  'bi_has_untagged_actuals',
] as const satisfies readonly (keyof IOPlanSummary)[];

export function PlanningProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, currentUser } = useAuth();
  const location = useLocation();
  const onPlanning = location.pathname.startsWith('/planning');
  const prevPathRef = useRef(location.pathname);

  const [availableCycles, setAvailableCycles] = useState<PlanningCycle[]>([]);
  const [selectedCycle, setSelectedCycleState] = useState<PlanningCycle | null>(null);
  const [expandedIOIds, setExpandedIOIds] = useState<Set<number>>(new Set());
  const [ioSummaries, setIOSummaries] = useState<IOPlanSummary[]>([]);
  const [summariesCycleId, setSummariesCycleId] = useState<number | null>(null);
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [summariesRefreshing, setSummariesRefreshing] = useState(false);
  const [summariesRefreshGeneration, setSummariesRefreshGeneration] = useState(0);
  const [cyclesLoading, setCyclesLoading] = useState(false);
  const [cyclesRefreshing, setCyclesRefreshing] = useState(false);

  const summariesCacheRef = useRef<Map<number, IOPlanSummary[]>>(new Map());
  // Live BI-compliance overrides pushed by the open IO editor, keyed by io_id
  // and scoped to the selected cycle (cleared on cycle change / logout). Applied
  // on top of any server/cached summaries so a stale stored bi_compliant flag
  // can't clobber the editor's live value. See BI_OVERRIDE_KEYS.
  const biOverridesRef = useRef<Map<number, Partial<IOPlanSummary>>>(new Map());
  const summariesRequestId = useRef(0);
  const cyclesRequestId = useRef(0);

  const applyBIOverrides = useCallback((list: IOPlanSummary[]): IOPlanSummary[] => {
    if (biOverridesRef.current.size === 0) return list;
    return list.map((s) => {
      const override = biOverridesRef.current.get(s.io_id);
      return override ? { ...s, ...override } : s;
    });
  }, []);

  const handleSetSelectedCycle = useCallback((cycle: PlanningCycle | null) => {
    setSelectedCycleState(cycle);
    if (cycle) localStorage.setItem(CYCLE_KEY, String(cycle.id));
    else localStorage.removeItem(CYCLE_KEY);
  }, []);

  const loadCycles = useCallback(
    async (options?: { silent?: boolean }) => {
      const reqId = ++cyclesRequestId.current;
      const hasCache = availableCycles.length > 0;
      const silent = options?.silent ?? hasCache;
      if (silent) {
        setCyclesRefreshing(true);
      } else {
        setCyclesLoading(true);
      }
      try {
        const cycles = currentUser?.is_admin
          ? await planningApi.getPlanningCycles()
          : await planningApi.getActiveCycles();
        if (cyclesRequestId.current !== reqId) return;

        const visibleCycles = cycles.filter((cycle) => cycle.status !== 'draft');
        setAvailableCycles(visibleCycles);
        if (visibleCycles.length > 0) {
          const urlCycleRaw = new URLSearchParams(window.location.search).get('cycle');
          const urlCycleId =
            urlCycleRaw && Number.isFinite(Number(urlCycleRaw)) ? Number(urlCycleRaw) : null;
          const urlCycle = urlCycleId != null ? visibleCycles.find((c) => c.id === urlCycleId) : null;
          const savedId = localStorage.getItem(CYCLE_KEY);
          const savedCycle = savedId ? visibleCycles.find((c) => c.id === Number(savedId)) : null;
          const openCycle = visibleCycles.find((c) => c.status === 'open') || visibleCycles[0];
          handleSetSelectedCycle(urlCycle || savedCycle || openCycle);
        } else {
          handleSetSelectedCycle(null);
        }
      } catch (err) {
        if (cyclesRequestId.current === reqId && !options?.silent) {
          console.error('Failed to load planning cycles:', err);
        }
      } finally {
        if (cyclesRequestId.current === reqId) {
          setCyclesLoading(false);
          setCyclesRefreshing(false);
        }
      }
    },
    [availableCycles.length, currentUser?.is_admin, handleSetSelectedCycle],
  );

  const loadIOSummaries = useCallback(
    async (cycleId: number, options?: { silent?: boolean; fresh?: boolean }) => {
      const reqId = ++summariesRequestId.current;
      const cached = summariesCacheRef.current.get(cycleId);
      const hasCache = cached !== undefined;
      const silent = options?.silent ?? hasCache;
      const fresh = options?.fresh ?? false;

      // Explicit fresh load = trust the server; drop live BI overrides so the
      // authoritative recomputed values take over.
      if (fresh) biOverridesRef.current.clear();

      if (silent) {
        setSummariesRefreshing(true);
      } else {
        setSummariesLoading(true);
      }

      try {
        const summaries = await planningApi.getIOSummaries(cycleId, { fresh });
        if (summariesRequestId.current === reqId) {
          summariesCacheRef.current.set(cycleId, summaries);
          if (selectedCycle?.id === cycleId) {
            setIOSummaries(applyBIOverrides(summaries));
            setSummariesCycleId(cycleId);
          }
        }
      } catch (err) {
        if (summariesRequestId.current === reqId && !silent) {
          console.error('Failed to load IO summaries:', err);
          if (selectedCycle?.id === cycleId) {
            setIOSummaries([]);
          }
        }
      } finally {
        if (summariesRequestId.current === reqId) {
          setSummariesLoading(false);
          setSummariesRefreshing(false);
        }
      }
    },
    [selectedCycle?.id, applyBIOverrides],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setAvailableCycles([]);
      setSelectedCycleState(null);
      setIOSummaries([]);
      setSummariesCycleId(null);
      setExpandedIOIds(new Set());
      summariesCacheRef.current.clear();
      biOverridesRef.current.clear();
      setCyclesLoading(false);
      setCyclesRefreshing(false);
      setSummariesLoading(false);
      setSummariesRefreshing(false);
      return;
    }
    if (onPlanning || availableCycles.length > 0) {
      void loadCycles({ silent: availableCycles.length > 0 });
    }
  }, [isAuthenticated, onPlanning]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated || !onPlanning) return;
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        void loadCycles({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isAuthenticated, onPlanning, loadCycles]);

  const selectedCycleId = selectedCycle?.id;
  useEffect(() => {
    // Overrides are per-cycle (IOs persist across cycles, so an override for
    // io_id X in cycle A must not leak into cycle B). Reset on every switch.
    biOverridesRef.current.clear();
    if (!selectedCycleId) {
      setIOSummaries([]);
      setSummariesCycleId(null);
      setSummariesLoading(false);
      return;
    }

    const cached = summariesCacheRef.current.get(selectedCycleId);
    setExpandedIOIds(new Set());
    if (cached !== undefined) {
      setIOSummaries(cached);
      // Cached rows are known to belong to this cycle.
      setSummariesCycleId(selectedCycleId);
      setSummariesLoading(false);
      void loadIOSummaries(selectedCycleId, { silent: true });
    } else {
      setIOSummaries([]);
      // Nothing loaded for this cycle yet — mark stale until the fetch lands.
      setSummariesCycleId(null);
      void loadIOSummaries(selectedCycleId);
    }
  }, [selectedCycleId, loadIOSummaries]);

  useEffect(() => {
    if (!isAuthenticated || !selectedCycleId) return;
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        void loadIOSummaries(selectedCycleId, { silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isAuthenticated, selectedCycleId, loadIOSummaries]);

  // Re-entering planning from another top-level route: show cache, refresh in background.
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    const enteredPlanning =
      location.pathname.startsWith('/planning') && !prev.startsWith('/planning');
    if (!enteredPlanning || !isAuthenticated) return;

    if (availableCycles.length > 0) {
      void loadCycles({ silent: true });
    }
    if (selectedCycleId && summariesCacheRef.current.has(selectedCycleId)) {
      void loadIOSummaries(selectedCycleId, { silent: true });
    }
  }, [location.pathname, isAuthenticated, availableCycles.length, selectedCycleId, loadCycles, loadIOSummaries]);

  const updateIOSummary = useCallback(
    (ioId: number, patch: Partial<IOPlanSummary>) => {
      // Snapshot any live BI-compliance fields so a later background
      // /io-summaries refresh (which may carry a stale bi_compliant flag)
      // can't overwrite them and cause the badge to flicker.
      const biPatch: Record<string, unknown> = {};
      for (const key of BI_OVERRIDE_KEYS) {
        if (key in patch) biPatch[key] = (patch as Record<string, unknown>)[key];
      }
      if (Object.keys(biPatch).length > 0) {
        biOverridesRef.current.set(ioId, {
          ...biOverridesRef.current.get(ioId),
          ...(biPatch as Partial<IOPlanSummary>),
        });
      }
      setIOSummaries((prev) => {
        const next = prev.map((s) => (s.io_id === ioId ? { ...s, ...patch } : s));
        if (selectedCycleId != null) {
          summariesCacheRef.current.set(selectedCycleId, next);
        }
        return next;
      });
    },
    [selectedCycleId],
  );

  const toggleIOExpand = useCallback((ioId: number) => {
    setExpandedIOIds((prev) => {
      const next = new Set(prev);
      if (next.has(ioId)) next.delete(ioId);
      else next.add(ioId);
      return next;
    });
  }, []);

  const ensureIOExpanded = useCallback((ioId: number) => {
    setExpandedIOIds((prev) => {
      if (prev.has(ioId)) return prev;
      const next = new Set(prev);
      next.add(ioId);
      return next;
    });
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedIOIds(new Set());
  }, []);

  const incrementSummariesRefreshGeneration = useCallback(() => {
    setSummariesRefreshGeneration((g) => g + 1);
  }, []);

  const refreshCycles = useCallback(
    () => loadCycles({ silent: availableCycles.length > 0 }),
    [loadCycles, availableCycles.length],
  );

  const contextValue = useMemo<PlanningContextValue>(() => ({
    availableCycles,
    selectedCycle,
    setSelectedCycle: handleSetSelectedCycle,
    expandedIOIds,
    toggleIOExpand,
    ensureIOExpanded,
    collapseAll,
    ioSummaries,
    summariesCycleId,
    loadIOSummaries,
    updateIOSummary,
    summariesLoading,
    summariesRefreshing,
    summariesRefreshGeneration,
    incrementSummariesRefreshGeneration,
    cyclesLoading,
    cyclesRefreshing,
    refreshCycles,
  }), [
    availableCycles,
    selectedCycle,
    handleSetSelectedCycle,
    expandedIOIds,
    toggleIOExpand,
    ensureIOExpanded,
    collapseAll,
    ioSummaries,
    summariesCycleId,
    loadIOSummaries,
    updateIOSummary,
    summariesLoading,
    summariesRefreshing,
    summariesRefreshGeneration,
    incrementSummariesRefreshGeneration,
    cyclesLoading,
    cyclesRefreshing,
    refreshCycles,
  ]);

  return (
    <PlanningContext.Provider value={contextValue}>
      {children}
    </PlanningContext.Provider>
  );
}

export function usePlanning(): PlanningContextValue {
  const ctx = useContext(PlanningContext);
  if (!ctx) throw new Error('usePlanning must be used within PlanningProvider');
  return ctx;
}
